import { useEffect, useState } from "react";
import { createPortal } from "react-dom";

interface PortalProps {
    children: React.ReactNode;
    containerId?: string;
    isModal?: boolean;
}

const Portal = ({
    children,
    containerId = "portal-root",
    isModal = false,
}: PortalProps) => {
    const [mounted, setMounted] = useState(false);

    useEffect(() => {
        setMounted(true);

        // Create portal container if it doesn't exist
        let portalContainer = document.getElementById(containerId);
        if (!portalContainer) {
            const div = document.createElement("div");
            div.id = containerId;

            // For modals, add specific positioning styles to reduce GPU memory usage
            if (isModal) {
                div.style.position = "fixed";
                div.style.top = "0";
                div.style.left = "0";
                div.style.width = "100%";
                div.style.height = "100%";
                div.style.zIndex = "9999";
                div.style.pointerEvents = "auto";
                div.style.overflow = "visible";

                // Add iOS specific optimizations
                if (
                    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
                    (navigator.platform === "MacIntel" &&
                        navigator.maxTouchPoints > 1)
                ) {
                    div.style.transform = "translateZ(0)";
                    div.style.webkitTransform = "translateZ(0)";
                    div.style.backfaceVisibility = "hidden";
                    div.style.webkitBackfaceVisibility = "hidden";
                }
            }

            document.body.appendChild(div);
            portalContainer = div;
        }

        return () => {
            // Clean up the portal container if it's empty
            const container = document.getElementById(containerId);
            if (container && container.childNodes.length === 0) {
                document.body.removeChild(container);
            }
        };
    }, [containerId, isModal]);

    return mounted
        ? createPortal(
              children,
              document.getElementById(containerId) || document.body
          )
        : null;
};

export default Portal;
